import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useCustomerCategories } from "@/hooks/useCustomerCategories";
import { useSavedCards } from "@/hooks/useSavedCards";
import { computeActivityJoinAmountMinor } from "@/lib/computeActivityJoinAmountMinor";
import { chargePendingPayment } from "@/lib/chargePendingPayment";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";
import type { PricingRule } from "@/lib/pricing";

// ─── Types ─────────────────────────────────────────────────────

export interface ActivityPlayer {
  id: string;
  user_id: string;
  status: string;
  joined_at: string;
  profile: {
    id: string;
    full_name: string | null;
    level: number | null;
    avatar_url: string | null;
  } | null;
}

export interface ActivityDetail extends Tables<"activities"> {
  courts: { name: string } | null;
  /** Booking ids linked to this activity (M1: drives hasLinkedBooking — the charge's booking gate). */
  linked_bookings?: { id: string }[] | null;
  activity_players: ActivityPlayer[];
  activity_type: {
    id: string;
    name: string;
    slug: string;
    icon: string;
    color: string;
  } | null;
  organizer_profile: {
    id: string;
    full_name: string | null;
    level: number | null;
    avatar_url: string | null;
  } | null;
}

// ─── Hook ──────────────────────────────────────────────────────

export function useActivityDetail(activityId: string | undefined) {
  const { user } = useAuth();
  const { t } = useTranslation("activities");
  const { organization } = useOrganization();
  const { hallId } = useOrgHall();
  const { categories } = useCustomerCategories(organization?.id);
  const { cards: savedCards } = useSavedCards();
  const qc = useQueryClient();

  // pricing_rules for the org's hall — needed to compute the fee-led join amount (2d-i).
  // `pricingRulesReady` (isSuccess) gates the join-charge compute: computeActivityJoinAmountMinor
  // MUST NOT run with empty pricingRules (calculateBookingPrice([]) → court 0 → fee-only undercharge).
  const { data: pricingRules = [], isSuccess: pricingRulesReady } = useQuery<PricingRule[]>({
    queryKey: ["pricing-rules-activity-join", hallId],
    queryFn: async () => {
      if (!hallId) return [];
      const { data } = await supabase.from("pricing_rules").select("*").eq("hall_id", hallId);
      return (data ?? []) as PricingRule[];
    },
    enabled: !!hallId,
  });

  const queryKey = ["activity-detail", activityId];

  // Fetch full activity with players + profiles
  const {
    data: activity,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      // Fetch the activity
      const { data: match, error: matchError } = await supabase
        .from("activities")
        // linked_bookings ids: M1's hasLinkedBooking signal (mirrors the charge's booking gate)
        .select("*, courts(name), linked_bookings:bookings!activity_id(id)")
        .eq("id", activityId!)
        .single();
      if (matchError) throw matchError;

      // Fetch players with profile join
      const { data: players, error: playersError } = await supabase
        .from("activity_players")
        .select("id, user_id, status, joined_at")
        .eq("activity_id", activityId!)
        .order("joined_at", { ascending: true });
      if (playersError) throw playersError;

      // Fetch profiles for all players
      const playerIds = (players ?? []).map((p) => p.user_id);
      // Include organizer in profile fetch
      const allIds = [...new Set([...playerIds, match.created_by])];
      const profiles: Record<string, ActivityPlayer["profile"]> = {};
      if (allIds.length > 0) {
        // Sprint 1 PR 5: drop unused `gender` column (ADMIN-only, never rendered)
        // and route through public_profiles view.
        const { data: profileData } = await supabase
          .from("public_profiles" as any)
          .select("id, full_name, level, avatar_url")
          .in("id", allIds);
        for (const p of profileData ?? []) {
          profiles[p.id] = p;
        }
      }

      // Fetch activity type if available
      let activityType: ActivityDetail["activity_type"] = null;
      if (match.activity_type_id) {
        const { data: typeData } = await supabase
          .from("activity_types" as any)
          .select("id, name, slug, icon, color")
          .eq("id", match.activity_type_id)
          .single();
        activityType = typeData as ActivityDetail["activity_type"];
      }

      const playersWithProfiles: ActivityPlayer[] = (players ?? []).map((p) => ({
        ...p,
        profile: profiles[p.user_id] ?? null,
      }));

      return {
        ...match,
        activity_players: playersWithProfiles,
        activity_type: activityType,
        organizer_profile: profiles[match.created_by] ?? null,
      } as ActivityDetail;
    },
    enabled: !!activityId,
  });

  // 2d-ii: the canonical join CHARGE amount (öre) for DISPLAY — the SAME computeActivityJoinAmountMinor
  // the join mutation charges below (one source, Lesson 51). The detail page must show THIS, never the
  // legacy `price_per_person` column (null for fee-led activities → it falsely rendered "Gratis" while
  // the player is actually charged court+fee). Reactive so display == charge. Null while loading /
  // non-system (the consumer treats null as "loading", 0 as genuinely free).
  // Root-cause C (gated): compute ONLY when every input is ready — hallId + orgId + the pricing-rules
  // query SUCCEEDED (pricingRulesReady). Computing with empty pricingRules yields the degraded fee-only
  // amount (court dropped). pricingRules.length is in the queryKey so a 0→N rules load forces a re-run.
  // pricingRulesReady is true even for a genuinely rule-less hall (isSuccess with []), so those still
  // compute (fee-only, correct) instead of hanging.
  const { data: joinChargeMinor = null } = useQuery({
    queryKey: ["activity-join-charge", activityId, user?.id, organization?.id, hallId, pricingRules.length],
    enabled:
      !!activity && activity.payment_mode === "system" && !!user?.id &&
      !!hallId && !!organization?.id && pricingRulesReady,
    queryFn: () =>
      computeActivityJoinAmountMinor({
        activity: {
          id: activity!.id,
          payment_mode: activity!.payment_mode,
          spots_total: activity!.spots_total,
          organizer_fee_per_person_minor: activity!.organizer_fee_per_person_minor,
          apply_member_discount: activity!.apply_member_discount,
          date: activity!.date,
          start_time: activity!.start_time,
          end_time: activity!.end_time,
        },
        userId: user?.id,
        orgId: organization?.id,
        hallId,
        pricingRules,
        categories,
        // M1: mirrors the charge's booking gate — no booking → 0 (genuinely uncharged), not a
        // hypothetical court price. Distinct from null (still loading).
        hasLinkedBooking: (activity?.linked_bookings?.length ?? 0) > 0,
      }),
  });

  // Derived state
  const activePlayers = (activity?.activity_players ?? []).filter(
    (p) => p.status === "joined"
  );
  const isOrganizer = user?.id === activity?.created_by;
  const hasJoined = activePlayers.some((p) => p.user_id === user?.id);
  const myPlayer = activePlayers.find((p) => p.user_id === user?.id);
  const isFull = activity ? activity.spots_filled >= activity.spots_total : false;
  const spotsLeft = activity ? activity.spots_total - activity.spots_filled : 0;

  const isPast = activity
    ? new Date(`${activity.date}T${activity.end_time}`) < new Date()
    : false;

  const isDeadlinePassed = activity?.registration_deadline
    ? new Date(activity.registration_deadline) < new Date()
    : false;

  // 2d-ii Step 4.4: has the current player PAID for this join? A paid join can't be left in-app
  // (leave_activity_v2 refuses it — refund = delspår 2e); the "Lämna" button is disabled + points to
  // the organizer. Two-step (no embed-filter): the activity's booking ids → any PAID booking_payment
  // by this user. Mirrors leave_activity_v2's own server-side paid-check. Only runs once joined.
  // 2e-core: the paid row itself (not just a boolean) — its amount_minor is the SINGLE source
  // for the refund display ("Lämna (återbetalas X kr)" = the amount that was DRAWN, not the
  // breakdown). hasPaidJoin derives from it.
  const { data: paidJoinPayment = null } = useQuery({
    queryKey: ["activity-paid-join", activityId, user?.id],
    enabled: !!activityId && !!user?.id && hasJoined,
    queryFn: async () => {
      const { data: bks } = await supabase.from("bookings").select("id").eq("activity_id", activityId!);
      const bookingIds = (bks ?? []).map((b) => b.id);
      if (bookingIds.length === 0) return null;
      const { data: paid } = await supabase
        .from("booking_payments")
        .select("id, amount_minor")
        .eq("user_id", user!.id)
        .eq("status", "paid")
        .in("booking_id", bookingIds)
        .limit(1);
      return ((paid ?? [])[0] ?? null) as { id: string; amount_minor: number | null } | null;
    },
  });
  const hasPaidJoin = !!paidJoinPayment;

  // 2e-core Policy-modell A: is NOW inside the (frozen) cancellation window — i.e. is there
  // still more than cancellation_hours left until the activity starts? Drives BOTH the
  // self-service refund-leave gate AND the "Gratis avbokning"-text (one source = enforcement).
  // cancellation_hours is frozen at create; NULL → 24 defensively (pre-backfill rows).
  const cancellationHours =
    (activity as { cancellation_hours?: number | null } | null)?.cancellation_hours ?? 24;
  const withinCancellationWindow = (() => {
    if (!activity?.date || !activity?.start_time) return false;
    const startMs = new Date(`${activity.date}T${activity.start_time}`).getTime();
    return Date.now() < startMs - cancellationHours * 3600_000;
  })();

  // 2d-iii B2: the player's UNPAID pending join payment (accept-without-card, redirect-abandon,
  // card decline — operator decision (b): the seat is kept, retry lives here). Drives the
  // "Slutför betalning" backstop CTA. Same two-step lookup as hasPaidJoin.
  const { data: pendingJoinPayment = null } = useQuery({
    queryKey: ["activity-pending-join-payment", activityId, user?.id],
    enabled: !!activityId && !!user?.id && hasJoined,
    queryFn: async () => {
      const { data: bks } = await supabase.from("bookings").select("id").eq("activity_id", activityId!);
      const bookingIds = (bks ?? []).map((b) => b.id);
      if (bookingIds.length === 0) return null;
      const { data: pending } = await supabase
        .from("booking_payments")
        .select("id, amount_minor, booking_id")
        .eq("user_id", user!.id)
        .eq("status", "pending")
        .in("booking_id", bookingIds)
        .limit(1);
      return (pending?.[0] ?? null) as { id: string; amount_minor: number | null; booking_id: string } | null;
    },
  });

  // 2d-iii B2: org kill-switch read for the CTA (plain RLS-verified member SELECT on
  // billing_config — NOT useBillingConfigs, whose insert-fallback must never run here).
  // charge_enabled !== true → the CTA is hidden (AP's unarmed state must feel normal).
  const { data: chargeEnabled = false } = useQuery({
    queryKey: ["billing-charge-enabled", organization?.id],
    enabled: !!organization?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("billing_config" as any)
        .select("charge_enabled")
        .eq("organization_id", organization!.id)
        .maybeSingle();
      return ((data as { charge_enabled?: boolean } | null)?.charge_enabled ?? false) === true;
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ["activities"] });
  };

  // ─── Join mutation (2d-i: converged to join_activity_v2) ───
  // The atomic RPC does the activity_players insert/reactivate + spots_filled bump +
  // (system-mode) pending booking_payments row in ONE transaction. user_id is forced
  // server-side; the org-gate replicates the RLS "Users can join activities" policy;
  // re-join is idempotent. The fee-led join amount (öre) is computed client-side via
  // computeActivityJoinAmountMinor (Lesson 38: price in one place) and passed in.
  // NO charge here — the pending row only (charge = 2d-ii).
  const joinMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !activityId) throw new Error(t("errors.missingUserInfo"));
      if (!activity) throw new Error(t("errors.activityNotLoaded"));

      const amountMinor = await computeActivityJoinAmountMinor({
        activity: {
          id: activity.id,
          payment_mode: activity.payment_mode,
          spots_total: activity.spots_total,
          organizer_fee_per_person_minor: activity.organizer_fee_per_person_minor,
          apply_member_discount: activity.apply_member_discount,
          date: activity.date,
          start_time: activity.start_time,
          end_time: activity.end_time,
        },
        userId: user.id,
        orgId: organization?.id,
        hallId,
        pricingRules,
        categories,
        hasLinkedBooking: (activity.linked_bookings?.length ?? 0) > 0,
      });

      const { data: joinData, error } = await (supabase.rpc as any)("join_activity_v2", {
        p_activity_id: activityId,
        p_amount_minor: amountMinor,
      });
      if (error) {
        const msg = error.message || "";
        if (/already_joined/.test(msg)) throw new Error(t("errors.alreadyJoined"));
        if (/not_a_member_of_org/.test(msg)) throw new Error(t("errors.mustBeMember"));
        if (/activity_full/.test(msg)) throw new Error(t("errors.activityFull", { defaultValue: "Aktiviteten är full" }));
        throw new Error(msg || t("errors.couldNotJoin"));
      }

      // 2d-ii Step 4: charge the pending row off-session with the player's saved MIT card.
      // join_activity_v2 returns {activity_id, payment_id}; chargePendingPayment marks the row paid
      // on success (Lesson 38: same charge primitive as booking + the /play dialog). No-card joins
      // are gated in Step 4.3, so the charge path is ALWAYS saved-card here — we only charge when a
      // card exists (no checkout-redirect branch for join). Outcomes:
      //   paid    → pending→paid (in the helper); player stays joined.
      //   skipped → charge_enabled=false (Step 3) → leave the pending row, player stays (AP's 2d-i state).
      //   failed  → decline/network → roll back the join atomically (leave_activity_v2) + surface the error.
      const paymentId = (joinData as { payment_id?: string } | null)?.payment_id ?? null;
      const mitCard = savedCards.find((c) => c.is_mit_enabled && c.is_default) ?? savedCards.find((c) => c.is_mit_enabled);
      if (paymentId && mitCard?.id && organization) {
        const charge = await chargePendingPayment({
          userId: user.id,
          orgId: organization.id,
          amountMinor,
          paymentId,
          savedCardId: mitCard.id,
          description: `Aktivitet: ${activity.activity_title ?? activityId}`,
          relatedEntityType: "activity",
          relatedEntityId: activityId,
        });
        if (charge.status === "failed") {
          await (supabase.rpc as any)("leave_activity_v2", { p_activity_id: activityId });
          invalidate();
          throw new Error(charge.network ? t("errors.paymentFailedRetry") : (charge.error || t("errors.paymentFailed")));
        }
        // paid / skipped → player stays joined (helper marked paid, or charge intentionally off).
      }
    },
    onSuccess: () => {
      invalidate();
      toast.success(t("toast.joinedMatch"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── Leave mutation (2d-ii Step 4.4: converged to leave_activity_v2) ───
  // The atomic RPC does status='left' + spots_filled-1 (clamp ≥0) + delete of the player's PENDING
  // payment row in ONE transaction, gated on the player actually being joined (Lesson 59) — replacing
  // the old 2 non-atomic client writes. It REFUSES a paid leave (paid_leave_requires_refund); the
  // "Lämna" button is gated on hasPaidJoin so a paid player never triggers the refusal, but we map
  // the error to a friendly message defensively.
  const leaveMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !activityId) throw new Error(t("errors.missingUserInfo"));
      const { error } = await (supabase.rpc as any)("leave_activity_v2", { p_activity_id: activityId });
      if (error) {
        const msg = error.message || "";
        if (/paid_leave_requires_refund/.test(msg)) {
          throw new Error(t("paidSpotContactOrganizer"));
        }
        throw new Error(msg || t("errors.couldNotLeave"));
      }
    },
    onSuccess: () => {
      invalidate();
      toast.success(t("toast.leftMatch"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── Refund-leave (2e-core, Policy-modell A) ───
  // Self-service: a PAID player inside the frozen cancellation window leaves AND is refunded.
  // Calls the user-auth refund-activity-leave edge fn (NOT the admin-gated refund-payment).
  // The fn refunds money first, then releases the seat (shared _release_activity_seat). A
  // seat_warning means the money is back but the seat needs admin attention (rider).
  const refundLeaveMutation = useMutation({
    mutationFn: async () => {
      if (!activityId) throw new Error(t("errors.missingUserInfo"));
      const { data, error } = await supabase.functions.invoke("refund-activity-leave", {
        body: { activity_id: activityId },
      });
      if (error) {
        // FunctionsHttpError carries the JSON body (outside_window etc.) on .context.
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === "function") {
          const body = await ctx.json().catch(() => null) as { error?: string } | null;
          if (body?.error === "outside_window") throw new Error(t("paidSpotContactOrganizer"));
        }
        throw new Error(t("errors.couldNotLeave"));
      }
      return data as { seat_warning?: boolean } | null;
    },
    onSuccess: (res) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["activity-paid-join", activityId, user?.id] });
      if (res?.seat_warning) {
        toast.success(t("refundedSeatWarning", {
          defaultValue: "Återbetalat — kontakta arrangören om platsen står kvar",
        }));
      } else {
        toast.success(t("refundedLeft", { defaultValue: "Du har lämnat aktiviteten — pengarna återbetalas" }));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── Complete payment (2d-iii B2 backstop CTA) ───
  // Settles the player's pending join payment per Modell A: saved MIT card → off-session
  // triple; no card → Stripe Checkout redirect (B1's guard makes an unarmed org a graceful
  // 'skipped', never a redirect attempt). failed keeps seat + pending (decision b) — the
  // CTA itself is the retry surface. Declined-card users retry the same card until they
  // change it under /profile (accepted v1 limitation).
  const completePaymentMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !activityId || !organization?.id) throw new Error(t("errors.missingUserInfo"));
      if (!pendingJoinPayment || !pendingJoinPayment.amount_minor) throw new Error(t("errors.couldNotJoin"));
      const amountMinor = pendingJoinPayment.amount_minor;
      const mitCard = savedCards.find((c) => c.is_mit_enabled && c.is_default) ?? savedCards.find((c) => c.is_mit_enabled);
      const description = `Aktivitet: ${activity?.activity_title ?? activityId}`;
      const charge = await chargePendingPayment({
        userId: user.id,
        orgId: organization.id,
        amountMinor,
        paymentId: pendingJoinPayment.id,
        savedCardId: mitCard?.id ?? null,
        description,
        relatedEntityType: "activity",
        relatedEntityId: activityId,
        bookingId: pendingJoinPayment.booking_id,
        successUrl: `${window.location.origin}/activities/${activityId}?payment=success`,
        cancelUrl: `${window.location.origin}/activities/${activityId}`,
      });
      if (charge.status === "redirect") {
        window.location.href = charge.url;
        return "redirect" as const;
      }
      if (charge.status === "paid") return "paid" as const;
      if (charge.status === "skipped") return "skipped" as const;
      throw new Error(t("errors.paymentDeclinedRetry", {
        defaultValue: "Betalningen nekades — försök igen via Slutför betalning, eller uppdatera ditt kort under Profil",
      }));
    },
    onSuccess: (outcome) => {
      if (outcome === "redirect") return;
      qc.invalidateQueries({ queryKey: ["activity-pending-join-payment", activityId, user?.id] });
      qc.invalidateQueries({ queryKey: ["activity-has-paid-join", activityId, user?.id] });
      invalidate();
      if (outcome === "paid") {
        toast.success(t("toast.paymentCompleted", { defaultValue: "Betalningen är genomförd!" }));
      } else {
        toast.success(t("toast.paymentLater", { defaultValue: "Betalningen hanteras senare." }));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── Approve player (organizer) ───
  const approveMutation = useMutation({
    mutationFn: async (playerId: string) => {
      const { error } = await supabase
        .from("activity_players")
        .update({ status: "joined" })
        .eq("id", playerId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Spelare godkänd");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── Reject player (organizer) ───
  const rejectMutation = useMutation({
    mutationFn: async (playerId: string) => {
      const { error } = await supabase
        .from("activity_players")
        .update({ status: "left" })
        .eq("id", playerId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Spelare nekad");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── Remove player (organizer) ───
  const removeMutation = useMutation({
    mutationFn: async (playerId: string) => {
      const { error } = await supabase
        .from("activity_players")
        .update({ status: "left" })
        .eq("id", playerId);
      if (error) throw error;

      if (activity) {
        const newFilled = Math.max(1, (activity.spots_filled ?? 2) - 1);
        await supabase
          .from("activities")
          .update({ spots_filled: newFilled, status: "open" })
          .eq("id", activityId!);
      }
    },
    onSuccess: () => {
      invalidate();
      toast.success("Spelare borttagen");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── Cancel activity (organizer) ───
  const cancelActivityMutation = useMutation({
    mutationFn: async () => {
      if (!activityId) throw new Error("Saknar aktivitets-ID");
      const { error } = await supabase
        .from("activities")
        .update({ status: "cancelled" })
        .eq("id", activityId);
      if (error) throw error;

      // Cancel associated bookings to free the courts
      // SPÅR 2-Pre: bookings column was renamed open_match_id → activity_id.
      await supabase
        .from("bookings")
        .update({ status: "cancelled" as const })
        .eq("activity_id", activityId);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Aktiviteten har ställts in");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ─── Update activity (organizer) ───
  const updateActivityMutation = useMutation({
    mutationFn: async (updates: Partial<Tables<"activities">>) => {
      if (!activityId) throw new Error("Saknar aktivitets-ID");
      const { error } = await supabase
        .from("activities")
        .update(updates)
        .eq("id", activityId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Aktiviteten uppdaterad");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return {
    activity,
    isLoading,
    error,
    activePlayers,
    isOrganizer,
    hasJoined,
    myPlayer,
    isFull,
    spotsLeft,
    isPast,
    isDeadlinePassed,
    joinChargeMinor,
    // 2d-ii Step 4.3: does the player have a chargeable MIT card? The detail-page "Gå med" gate
    // (ActivityActions) disables join when a charge applies but no card is saved + links to /profile.
    hasMitCard: savedCards.some((c) => c.is_mit_enabled),
    // 2d-ii Step 4.4: has the player PAID this join? Disables the "Lämna" button (paid leave needs
    // a refund — delspår 2e — so it routes to the organizer instead).
    hasPaidJoin,
    // 2e-core: refund-leave gating + display amount (the DRAWN amount, one source).
    withinCancellationWindow,
    refundChargeMinor: paidJoinPayment?.amount_minor ?? null,
    // 2d-iii B2: unpaid pending join → "Slutför betalning" CTA (hidden when the org is unarmed).
    hasPendingJoin: !!pendingJoinPayment,
    pendingChargeMinor: pendingJoinPayment?.amount_minor ?? null,
    chargeEnabled,

    join: joinMutation.mutate,
    leave: leaveMutation.mutate,
    refundLeave: refundLeaveMutation.mutate,
    completePayment: completePaymentMutation.mutate,
    approve: approveMutation.mutate,
    reject: rejectMutation.mutate,
    removePlayer: removeMutation.mutate,
    cancelActivity: cancelActivityMutation.mutate,
    updateActivity: updateActivityMutation.mutate,

    isJoining: joinMutation.isPending,
    isLeaving: leaveMutation.isPending,
    isRefundLeaving: refundLeaveMutation.isPending,
    isCompletingPayment: completePaymentMutation.isPending,
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isRemoving: removeMutation.isPending,
    isCancelling: cancelActivityMutation.isPending,
    isUpdating: updateActivityMutation.isPending,
  };
}
